// Gemeinsame Helfer/Konstanten für die Warenwirtschaft (WMS) — ausgelagert aus WMSPage.jsx.

export const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
export const fmtNum = (v) => Number(v || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });

export const CATEGORY_LABELS = {
    part: { label: "Ersatzteile", color: "bg-blue-100 text-blue-800" },
    tire: { label: "Reifen", color: "bg-amber-100 text-amber-800" },
    accessory: { label: "Zubehör", color: "bg-purple-100 text-purple-800" },
    fluid: { label: "Flüssigk.", color: "bg-cyan-100 text-cyan-800" },
    labour: { label: "Lohn", color: "bg-gray-100 text-gray-700" },
    // Feb-2026 (User-Wunsch) — zusätzliche Warenwirtschafts-Kategorien
    insurance_plate: { label: "Vers.-Kennz.", color: "bg-emerald-100 text-emerald-800" },
    sales: { label: "Verkauf/Vertrieb", color: "bg-indigo-100 text-indigo-800" },
    gw_damage: { label: "GW-Preisliste", color: "bg-red-100 text-red-800" },
    // Jun-2026 (User-Wunsch)
    package: { label: "Paket/Set", color: "bg-fuchsia-100 text-fuchsia-800" },
    manufacturer_action: { label: "Herstelleraktion", color: "bg-orange-100 text-orange-800" },
    miet_ersatzfahrzeug: { label: "Miet / Ersatzfahrzeug", color: "bg-teal-100 text-teal-800" },
    // Jul-2026 (User-Wunsch) — Kategorie für Artikel mit festem Endkunden-Preis
    festpreis: { label: "Festpreise", color: "bg-lime-100 text-lime-800" },
    werkstatt_service: { label: "Werkstatt-Servicepreise", color: "bg-cyan-100 text-cyan-800" },
    other: { label: "Sonst.", color: "bg-gray-100 text-gray-700" },
};

// Jun-2026 (User-Wunsch) — Single Source aller WMS-Kategorien (Reihenfolge =
// Anzeige in VK-Kalkulation, Abschlags-Tabellen, Artikel-Dialog).
export const WMS_CATEGORY_OPTIONS = [
    { v: "part", l: "Ersatzteile" },
    { v: "tire", l: "Reifen" },
    { v: "accessory", l: "Zubehör" },
    { v: "fluid", l: "Betriebsstoffe" },
    { v: "labour", l: "Arbeit" },
    { v: "insurance_plate", l: "Versicherungskennzeichen" },
    { v: "sales", l: "Verkauf/Vertrieb" },
    { v: "gw_damage", l: "GW-Preisliste" },
    { v: "package", l: "Paket / Set" },
    { v: "manufacturer_action", l: "Herstelleraktion" },
    { v: "miet_ersatzfahrzeug", l: "Miet / Ersatzfahrzeug" },
    { v: "festpreis", l: "Festpreise" },
    { v: "werkstatt_service", l: "Werkstatt-Servicepreise" },
    { v: "other", l: "Sonstiges" },
];

// Feb-2026 (User-Wunsch) — Marge-Ampel: Ist-Marge (VK vs. EK) gegen den
// Soll-Aufschlag aus den passenden Aufschlags-Regeln. Grün ≥ Soll, Gelb ≥ 80 %
// vom Soll, sonst Rot. Ohne EK oder ohne passende Regel: neutral.
export function computeMarginStatus(a, rules) {
    const ekRaw = Number(a.purchase_price_net || 0);
    // Jun-2026 (User-Wunsch) — Kommissions-Abschlag senkt den effektiven EK
    // (bessere Marge), verändert aber nicht den VK.
    const disc = Number(a.consignment_discount_percent || 0);
    const ek = (a.ownership === "consignment" && disc > 0)
        ? ekRaw * (1 - disc / 100)
        : ekRaw;
    const vatF = 1 + (Number(a.vat_rate || 19) / 100);
    const vkNet = Number(a.sell_price_net || (a.sell_price_gross ? a.sell_price_gross / vatF : 0));
    if (ek <= 0 || vkNet <= 0) return { level: "none", istPct: null, sollPct: null };
    const istPct = ((vkNet - ek) / ek) * 100;
    const matches = (rules || []).filter((r) => {
        if (r.category && r.category !== a.category) return false;
        if (ekRaw < Number(r.ek_min || 0)) return false;
        if (r.ek_max != null && ekRaw > Number(r.ek_max)) return false;
        return true;
    }).sort((x, y) => (y.category ? 1 : 0) - (x.category ? 1 : 0) || Number(y.ek_min || 0) - Number(x.ek_min || 0));
    const sollPct = matches.length ? Number(matches[0].markup_percent) : null;
    if (sollPct == null) return { level: "neutral", istPct, sollPct: null };
    let level = "red";
    if (istPct >= sollPct) level = "green";
    else if (istPct >= sollPct * 0.8) level = "yellow";
    return { level, istPct, sollPct };
}

// Nov-2026 (User-Wunsch) — Etiketten-Sammelliste: Artikel aus der Artikel-
// Tabelle mit einem Klick aufs Etiketten-PDF setzen (Tab „Preisetiketten").
export const LABEL_QUEUE_KEY = "tw_label_queue";
export function getLabelQueue() {
    try { return JSON.parse(localStorage.getItem(LABEL_QUEUE_KEY) || "[]"); } catch { return []; }
}
export function addToLabelQueue(a) {
    const q = getLabelQueue();
    if (!q.some((x) => x.id === a.id)) {
        q.push({ id: a.id, sku: a.sku, name: a.name, location: a.location || null,
            sell_price_gross: a.sell_price_gross ?? null, stock_qty: a.stock_qty ?? null });
        localStorage.setItem(LABEL_QUEUE_KEY, JSON.stringify(q));
    }
    return q.length;
}
export function clearLabelQueue() { localStorage.removeItem(LABEL_QUEUE_KEY); }
